import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import Icon from '../../components/Icon';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';

/**
 * "About this project".
 *
 * Copy and layout follow "about this project.jpg". The team is grouped rather
 * than listed flat — founders, then the build team, then mentors — with the
 * blank rows the reference shows between each group.
 */

const CG_LINKEDIN = 'https://www.linkedin.com/company/commongroundorg/';
const CONTACT_EMAIL = 'mailto:info@commonground.boston';

interface Person {
  name: string;
  role: string;
  href?: string;
}

/** Each inner array is one group; groups are separated by a blank row. */
const TEAM: Person[][] = [
  [
    { name: 'Sam Owen', role: 'Co-Founder', href: 'https://www.linkedin.com/in/samuel-owen/' },
    { name: 'Anna Mortara', role: 'Co-Founder', href: 'https://www.linkedin.com/in/anna-mortara/' },
  ],
  [
    { name: 'Hongjin Lin', role: 'Research + Tech Advisor' },
    { name: 'Conner Owen', role: 'Tech Lead (Frontend)', href: 'https://www.linkedin.com/in/chdowen/' },
    { name: 'Danielle Knutson', role: 'Tech Lead (Backend)', href: 'https://www.linkedin.com/in/danielle-knutson' },
    { name: 'Adam Gilbert-Diamond', role: 'Outreach Lead' },
  ],
  [
    { name: 'Eric deMenthon', role: 'Mentor' },
    { name: 'Lauren Tyger', role: 'Mentor' },
  ],
];

const Panel = styled('div')(({ theme }) => ({
  background: BRAND.mochaDeep,
  color: BRAND.parchment,
  padding: '34px 44px 44px',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
  gap: 52,
  alignItems: 'start',
  [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr', gap: 30, padding: '26px 24px 32px' },
}));

/**
 * Drops the right column to the first line of body copy, so the CommonGround
 * mark's top edge lines up with "Hey there!" rather than with the heading —
 * matching "about this project.jpg".
 *
 * The offset is the heading's own height: 31px at line-height 1.15, plus its
 * 24px bottom margin.
 */
const TeamColumn = styled('div')(({ theme }) => ({
  paddingTop: 31 * 1.15 + 24,
  [theme.breakpoints.down('md')]: { paddingTop: 0 },
}));

const Body = styled('p')({
  margin: '0 0 18px',
  fontFamily: FONTS.body,
  fontSize: 14.5,
  lineHeight: 1.62,
  color: 'rgba(243,238,231,0.9)',
  maxWidth: '64ch',
});

const ColHead = styled('h3')({
  margin: '0 0 12px',
  fontFamily: FONTS.display,
  fontWeight: 700,
  fontSize: 18,
  color: BRAND.parchment,
});

const NameRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.15fr)',
  gap: 10,
  fontFamily: FONTS.body,
  fontSize: 12,
  lineHeight: 1.5,
  padding: '2px 0',
});

const InlineLink = styled('a')({
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  textDecorationColor: 'rgba(243,238,231,0.4)',
  '&:hover': { textDecorationColor: BRAND.parchment },
});

const LogoLink = styled('a')({
  display: 'block',
  width: 118,
  color: BRAND.parchment,
  transition: 'opacity 120ms ease',
  '&:hover': { opacity: 0.75 },
});

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function AboutModal({ open, onClose }: Props) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: { sx: { background: BRAND.mochaDeep, backgroundImage: 'none', borderRadius: 4 } },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: 'absolute', top: 14, right: 16, color: BRAND.parchment, zIndex: 1 }}
        >
          ✕
        </IconButton>

        <Panel>
          <div>
            <Typography
              component="h2"
              sx={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 31,
                lineHeight: 1.15,
                mb: 3,
                letterSpacing: '-0.01em',
              }}
            >
              About this project
            </Typography>

            <Body>
              Hey there! We're a team of MIT + Harvard students who's building a climate action
              events aggregator called{' '}
              <InlineLink href={CG_LINKEDIN} target="_blank" rel="noopener noreferrer">
                <strong>CommonGround</strong>
              </InlineLink>
              . Our platform amplifies local climate actions in your area, connects you with your
              fellow local climate people, and connects you with hyper-local actions that match
              your interests.
            </Body>

            <Body>
              As we've been building our schedules to attend this year's New York Climate Week, we
              noticed that we were struggling with the same issues we found in local climate
              actions — diffused information, scattered across a dozen calendars, and no way to
              tell which events were actually worth crossing town for.
            </Body>

            <Body>
              That's why we built <strong>My Climate Week</strong> — because, like climate actions,
              planning your Climate Week should be joyful, not stressful.
            </Body>

            <Body>
              Has this tool helped you plan your Climate Week? Our platform is capable of far more
              than planning for one single conference —{' '}
              <InlineLink href={CONTACT_EMAIL}>
                <strong>reach out to us</strong>
              </InlineLink>{' '}
              and let's chat about how our platform can help your municipality, company, NGO, or
              team make climate action organizing easy, impactful, and joyful!
            </Body>

            <Typography
              sx={{
                fontFamily: FONTS.body,
                fontSize: 12.5,
                lineHeight: 1.55,
                color: 'rgba(243,238,231,0.6)',
                mt: 3,
                maxWidth: '64ch',
              }}
            >
              P.S. This platform saves your schedule locally to your browser, so if you clear your
              browsing history, you'll lose your saved events!
            </Typography>
          </div>

          <TeamColumn>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 2.2 }}>
              <LogoLink
                href={CG_LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="CommonGround on LinkedIn"
              >
                <Icon name="logo:cg" size="100%" title="CommonGround" />
              </LogoLink>
              <ColHead style={{ margin: 0 }}>team:</ColHead>
            </Box>

            <Box sx={{ mb: 4 }}>
              {TEAM.map((group, i) => (
                <Box key={group[0].name} sx={{ mt: i === 0 ? 0 : 2 }}>
                  {group.map((person) => (
                    <NameRow key={person.name}>
                      <span style={{ fontWeight: 700 }}>
                        {person.href ? (
                          <InlineLink href={person.href} target="_blank" rel="noopener noreferrer">
                            {person.name}
                          </InlineLink>
                        ) : (
                          person.name
                        )}
                      </span>
                      <span style={{ opacity: 0.75 }}>{person.role}</span>
                    </NameRow>
                  ))}
                </Box>
              ))}
            </Box>

            <ColHead>Event data courtesy of:</ColHead>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 700 }}>
                <InlineLink
                  href="https://www.climateweeknyc.org/event-search"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  NYCW Official Events Search
                </InlineLink>
              </Typography>
              <Typography sx={{ fontFamily: FONTS.body, fontSize: 10.5, opacity: 0.72, mt: 0.2 }}>
                by The Climate Group
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontFamily: FONTS.body, fontSize: 12.5, fontWeight: 700 }}>
                <InlineLink
                  href="https://airtable.com/appgmmzx92dXykNJB/shrQsxPfw0RvlKKED?utm_source=substack&utm_medium=email"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  The Great Climate Week NYC Guide
                </InlineLink>
              </Typography>
              {/* Both credits hang under one "by", indented to align with each
                  other, as in the reference. */}
              <Typography sx={{ fontFamily: FONTS.body, fontSize: 10.5, opacity: 0.72, mt: 0.2 }}>
                by{' '}
                <InlineLink
                  href="https://climatefilmfest.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Alec Turnbull (CFF)
                </InlineLink>
              </Typography>
              <Typography
                sx={{ fontFamily: FONTS.body, fontSize: 10.5, opacity: 0.72, pl: '1.35em' }}
              >
                <InlineLink
                  href="https://www.streetlifeventures.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Sonam Velani (Streetlife Ventures)
                </InlineLink>
              </Typography>
            </Box>
          </TeamColumn>
        </Panel>
      </Box>
    </Dialog>
  );
}
